import { useEffect, useState } from "react";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import { Box, Card, Grid, Button, Typography } from "@mui/material";
import {
  DATE_FORMATS,
  formatDate,
} from "../../../services/date-and-time.utils";
import { showNotification } from "../../../services/utils";
import MeetingSummary from "./MeetingSummary";
import ZoomMeetingModal from "./ZoomMeetingModal";
import FlexBox from "../../layouts/flexbox";
import {
  ThumbnailBox,
  ThumbnailImg,
  ThumbnailPlaceholder,
  PlayRecordingButton,
} from "./recordingThumbnailStyles";

interface RecordingCardProps {
  data: any;
}

function getFilenameFromUrl(url: string): string {
  try {
    const pathname = new URL(url).pathname;
    const last = pathname.split("/").filter(Boolean).pop();
    return last || "download";
  } catch {
    const parts = url.split("?")[0].split("/");
    return parts[parts.length - 1] || "download";
  }
}

async function downloadFile(url: string, label: string) {
  if (!url) {
    showNotification({
      message: `${label} not available for this recording.`,
      type: "error",
    });
    return;
  }
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error("Failed");
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = getFilenameFromUrl(url);
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objectUrl);
  } catch {
    showNotification({
      message: `Failed to download ${label}. Please try again later.`,
      type: "error",
    });
  }
}

export default function RecordingCard({ data }: RecordingCardProps) {
  const [showRecording, setShowRecording] = useState(false);
  const [isThumbnailLoaded, setIsThumbnailLoaded] = useState(false);
  const [thumbFailed, setThumbFailed] = useState(false);

  const recordingDownloadUrl =
    data?.zoomRecordingDetails?.downloadUrls?.video || "";
  const remoteThumbUrl = data?.thumbnailUrl?.trim?.() || "";

  useEffect(() => {
    setThumbFailed(false);
    setIsThumbnailLoaded(false);
  }, [remoteThumbUrl]);
  const zoomRecordingUrl = data?.zoomRecordingDetails?.recordings || "";
  const downloadUrlsMap = data?.zoomRecordingDetails?.downloadUrls || {};
  const transcriptKey = Object.keys(downloadUrlsMap).find((k) =>
    k.includes("audio_transcript")
  );
  const transcriptDownloadUrl = transcriptKey
    ? downloadUrlsMap[transcriptKey]
    : "";
  const userData = {
    ambassador: data?.ambassador,
    client: data?.client,
  };

  const attendeeLabel = [data?.name, data?.title].filter(Boolean).join(", ");
  const slot = data?.selectedTimeSlotRaw;
  const dateFormatted = slot?.from
    ? formatDate(slot.from, DATE_FORMATS.fullDateWithDayWithoutDay)
    : "-";
  const timeFormatted =
    slot?.from && slot?.to
      ? `${formatDate(slot.from, DATE_FORMATS.hourMinWithAMPM)} - ${formatDate(
          slot.to,
          DATE_FORMATS.hourMinWithAMPM
        )}`
      : "-";

  return (
    <>
      <Card sx={{ p: 2, pb: 0 }}>
        <Grid container spacing={2}>
          <Grid
            item
            xs={12}
            lg={6}
            xl={4.5}
            sx={{
              borderRight: { xs: "none", md: 1 },
              borderColor: { md: "divider" },
              p: 2,
            }}
          >
            <FlexBox gap={2} flexDirection={{ xs: "column", sm: "row" }}>
              <ThumbnailBox>
                <ThumbnailPlaceholder />
                {remoteThumbUrl && !thumbFailed ? (
                  <ThumbnailImg
                    src={remoteThumbUrl}
                    alt=""
                    loading="lazy"
                    onLoad={() => setIsThumbnailLoaded(true)}
                    onError={() => {
                      setThumbFailed(true);
                      setIsThumbnailLoaded(false);
                    }}
                    $loaded={isThumbnailLoaded}
                  />
                ) : null}
                <PlayRecordingButton
                  disableRipple
                  startIcon={<PlayArrowIcon />}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    setShowRecording(true);
                  }}
                >
                  Play Recording
                </PlayRecordingButton>
              </ThumbnailBox>
              <FlexBox flexDirection="column" gap={1.5} flex={1}>
                {Boolean(attendeeLabel) && (
                  <FlexBox gap={1.5} alignItems="center">
                    <AccountCircleOutlinedIcon
                      fontSize="medium"
                      color="action"
                    />
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      title={attendeeLabel}
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {attendeeLabel}
                    </Typography>
                  </FlexBox>
                )}
                {Boolean(data?.company) && (
                  <FlexBox gap={1.5} alignItems="center">
                    <BusinessOutlinedIcon fontSize="medium" color="action" />
                    <Typography variant="body2" fontWeight={500}>
                      {data.company}
                    </Typography>
                  </FlexBox>
                )}
                <FlexBox gap={1.5} alignItems="center">
                  <CalendarTodayOutlinedIcon
                    fontSize="medium"
                    color="action"
                  />
                  <Typography variant="body2" fontWeight={500}>
                    {dateFormatted}
                  </Typography>
                </FlexBox>
                <FlexBox gap={1.5} alignItems="center">
                  <AccessTimeOutlinedIcon fontSize="medium" color="action" />
                  <Typography variant="body2" fontWeight={500}>
                    {timeFormatted}
                  </Typography>
                </FlexBox>
                <FlexBox gap={1.5} flexWrap="wrap" mt="auto" pt={1}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<FileDownloadOutlinedIcon />}
                    onClick={() =>
                      downloadFile(recordingDownloadUrl, "Recording")
                    }
                  >
                    Recording
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<FileDownloadOutlinedIcon />}
                    onClick={() =>
                      downloadFile(transcriptDownloadUrl, "Transcript")
                    }
                  >
                    Transcript
                  </Button>
                </FlexBox>
              </FlexBox>
            </FlexBox>
          </Grid>
          <Grid item xs={12} lg={6} xl={7.5} sx={{ p: { md: 2 } }}>
            <MeetingSummary data={data?.meetingSummary} />
          </Grid>
        </Grid>
      </Card>

      <ZoomMeetingModal
        open={showRecording}
        videoUrl={zoomRecordingUrl}
        vttUrl={transcriptDownloadUrl}
        downloadUrls={downloadUrlsMap}
        summaryData={data?.meetingSummary}
        userData={userData}
        onClose={() => setShowRecording(false)}
      />
    </>
  );
}
